import {
  FormHandler,
  RelationEditor,
  showSuccessApplyMessage,
} from '@framework/form';
import { Drawer } from '@framework/ui';
import { yup } from '@framework/validation';
import { Button, ButtonProps, message } from 'antd';
import React, { useMemo, useState } from 'react';
import { DrawerEditorButtonRow } from '../../../../../../ui/drawer';
import { WidgetType } from '../../../../Widget';
import { WidgetSelect } from '../../../../select/WidgetSelect';
import { WidgetSelectFilters } from '../../../../select/WidgetSelectFilters';
import { useConfigValidationContext } from '../../../../validation/hooks/useConfigValidationContext';
import { WidgetRepository } from '../../../WidgetRepository';
import { ITableCell, ITableWidgetCell, TableCellType } from '../ITableCell';
import { TableWidgetConfigForm } from '../common/TableWidgetConfigForm';

const defaultValidationSchema = yup
  .object()
  .noUnknown() as yup.ObjectSchema<any>;

export interface ITableCellWidgetEditDrawerProps {
  editor: RelationEditor<ITableCell>;
  buttonProps?: Omit<ButtonProps, 'onClick'>;
}

/**
 * Drawer per la configurazione di una cella di una tabella, ovvero
 * per le celle **personalizzate** all'interno di una Riga custom.
 *
 * Nel Form gestiamo la configurazione del **solo Widget**, non di tutta
 * la cella, per questo il validatore è `widgetValidator`
 */
export function TableCellWidgetEditDrawer(
  props: ITableCellWidgetEditDrawerProps,
) {
  const { editor, buttonProps } = props;

  const [selectedType, setSelectedType] = useState<WidgetType | null>(null);
  const widgetType = selectedType ? WidgetRepository.find(selectedType) : null;

  const current = editor.handle.current;
  const widget = current?.type === TableCellType.Widget ? current.widget : null;

  const widgetValidator = useMemo(
    () =>
      WidgetRepository.getConfigValidationSchema(widget) ??
      defaultValidationSchema,
    [widget],
  );

  const validationContext = useConfigValidationContext({ isDraft: true });

  return (
    <>
      <WidgetSelect
        width="60%"
        filter={WidgetSelectFilters.TableCell}
        onChange={value => {
          setSelectedType(value);
        }}
      />
      <Button
        children="+ Aggiungi Cella"
        {...buttonProps}
        disabled={!selectedType}
        onClick={() => {
          if (!widgetType) return;

          const widget = WidgetRepository.getInitial(widgetType.type, {
            label: [{ type: 'text', text: 'Nuova cella' }],
            code: `cella_${editor.items.length}`,
          });

          if (!widget) return;

          editor.handle.open({ type: TableCellType.Widget, widget });
        }}
      />
      <Drawer
        maskClosable={false}
        title="Configurazione Cella"
        size="large"
        placement="right"
        onClose={editor.handle.cancel}
        open={editor.handle.isVisible}
      >
        <FormHandler
          layout="vertical"
          schema={widgetValidator}
          initialValues={widget ?? {}}
          hideVisualFeedback
          validationContext={validationContext}
          onSubmit={async rawValues => {
            try {
              const values = await widgetValidator.validate(rawValues, {
                context: validationContext,
              });

              const result = editor.handle.apply({
                ...(editor.handle.current as ITableWidgetCell),
                widget: {
                  ...widget,
                  ...values,
                },
              });
              showSuccessApplyMessage(result, {
                onAdd: 'Cella aggiunta con successo',
                onUpdate: 'Cella modificata con successo',
              });
            } catch (e) {
              // eslint-disable-next-line @typescript-eslint/no-floating-promises
              message.error('Si è verificato un errore nella gestione della cella'); // prettier-ignore
              console.error(e);
            }
          }}
        >
          {widget && <TableWidgetConfigForm widget={widget} />}
          <DrawerEditorButtonRow editor={editor} />
        </FormHandler>
      </Drawer>
    </>
  );
}
